// Dashboard.js - Load loan details from localStorage only
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Logout } from '@mui/icons-material';
import { 
  Card as BootstrapCard, 
  CardBody, 
  Button, 
  Modal, 
  Spinner, 
 
} from "react-bootstrap";
import { 
  Container, 
  Grid, 
  Card as MuiCard, 
  CardContent, 
  Typography, 
  Chip, 
  Box, 
  Alert, 
  Divider,
  useMediaQuery,
  useTheme,
  IconButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import termsvg from "./term.svg";
import Flipcard from "./Flipcard";
import "../../App.css";
// import ResponsiveText from "../../components/ResponsiveText";
import AppLayout from "../../layouts/AppLayout";

// ==================== STYLED COMPONENTS ====================

const StyledLoanCard = styled(MuiCard)(({ theme, active }) => ({
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  border: active ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  boxShadow: active ? theme.shadows[8] : theme.shadows[2],
  "&:hover": {
    boxShadow: theme.shadows[6],
    transform: "translateY(-4px) scale(1.02)",
    borderColor: theme.palette.primary.light,
  },
  "&:active": {
    transform: "translateY(-2px) scale(1.01)",
  }
}));

// ==================== CUSTOM HOOKS ====================

const useTokenManagement = () => {
  const location = useLocation();
  const [authState, setAuthState] = useState({
    token: null,
    isValidated: false,
    isExpired: false,
  });

  useEffect(() => {
    const tokenFromState = location.state?.token;
    const tokenFromStorage = localStorage.getItem("authToken");
    const currentToken = tokenFromState || tokenFromStorage;

    if (!currentToken || currentToken.length < 10) {
      setAuthState({ token: null, isValidated: false, isExpired: true });
      return;
    }

    if (tokenFromState && !tokenFromStorage) {
      localStorage.setItem("authToken", tokenFromState);
    }

    setAuthState({
      token: currentToken,
      isValidated: true,
      isExpired: false,
    });
  }, [location]);

  const clearAuthData = useCallback(() => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("cust_id_selected");
    localStorage.removeItem("selected_customer_data");
    localStorage.removeItem("customerData");
    localStorage.removeItem("sessionStartTime");
    localStorage.removeItem("customer_id");
    localStorage.removeItem("mobile");
    
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith("Local_Loandetails")) {
        localStorage.removeItem(key);
      }
    });
    
    setAuthState({ token: null, isValidated: false, isExpired: false });
  }, []);

  return { ...authState, clearAuthData };
};

const useUserData = () => {
  return useMemo(() => {
    try {
      const selectedCustomerData = localStorage.getItem("selected_customer_data");
      if (!selectedCustomerData) return null;
      
      const customer = JSON.parse(selectedCustomerData);
      
      if (!customer.customer_id || !customer.customer_name || !customer.customer_phone) {
        return null;
      }
      
      return {
        customerID: customer.customer_id,
        customerName: customer.customer_name,
        cellNumber: customer.customer_phone,
        email: customer.email || "",
      };
    } catch {
      return null;
    }
  }, []);
};

const useLoanData = (customerID) => {
  return useMemo(() => {
    if (!customerID) return null;
    try {
      const loanKey = `Local_Loandetails${customerID}`;
      const loanDataString = localStorage.getItem(loanKey);
      if (!loanDataString) return null;
      
      const parsed = JSON.parse(loanDataString);
      
      if (parsed?.is_success && parsed?.data) {
        return {
          ...parsed.data,
          gateways: parsed.data.gateways || {}
        };
      }
      
      return null;
    } catch (error) {
      console.error("❌ [Dashboard] Error parsing loan data:", error);
      return null;
    }
  }, [customerID]);
};

const useBackButtonHandler = () => {
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/dashboard" && location.pathname !== "/dashboard/") return;

    const handleBackButton = (event) => {
      event.preventDefault();
      setShowModal(true);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBackButton);
    return () => window.removeEventListener("popstate", handleBackButton);
  }, [location.pathname]);

  return { showModal, setShowModal };
};



// ==================== MAIN COMPONENT ====================

export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const authState = useTokenManagement();
  const user = useUserData();
  const { showModal, setShowModal } = useBackButtonHandler();
  const loanData = useLoanData(user?.customerID);

  const [selectedLoans, setSelectedLoans] = useState(null);
  const [toggle, setToggle] = useState("");
  const [isInitializing, setIsInitializing] = useState(true);

  const loanCardsRef = useRef(null);

  useEffect(() => {
    if (user?.customerID) {
      console.log("📂 [Dashboard] Loading loan details from localStorage");
      console.log("👤 [Dashboard] Customer ID:", user.customerID);
      
      const loanKey = `Local_Loandetails${user.customerID}`;
      const storedData = localStorage.getItem(loanKey);
      
      if (storedData) {
        console.log("✅ [Dashboard] Loan data found in localStorage");
        try {
          const parsed = JSON.parse(storedData);
          console.log("📊 [Dashboard] Loan types:", parsed?.data?.loan_types?.length || 0);
          console.log("💳 [Dashboard] Available gateways:", parsed?.data?.gateways);
          
          if (parsed?.data?.accounts) {
            const accountSummary = Object.entries(parsed.data.accounts).map(([key, loans]) => ({
              type: key,
              count: loans.length
            }));
            console.log("💰 [Dashboard] Account summary:", accountSummary);
          }
        } catch (e) {
          console.error("❌ [Dashboard] Error parsing loan data:", e);
        }
      } else {
        console.warn("⚠️ [Dashboard] No loan data in localStorage for key:", loanKey);
        console.log("📝 [Dashboard] Available localStorage keys:", 
          Object.keys(localStorage).filter(k => k.startsWith('Local_Loandetails'))
        );
      }
    }
    
    setIsInitializing(false);
  }, [user?.customerID]);

  const handleSignOut = useCallback(() => {
    console.log("🚪 [Dashboard] User signing out");
    authState.clearAuthData();
    sessionStorage.clear();
    navigate("/", { replace: true });
  }, [authState, navigate]);

  const handleLeave = useCallback(() => {
    setShowModal(false);
    handleSignOut();
  }, [setShowModal, handleSignOut]);

// Dashboard.js - Updated handlePayment to accept extra data
const handlePayment = useCallback((payAmount, loan, extraData = {}) => {
  console.log("═══════════════════════════════════════");
  console.log("💳 [Dashboard] Payment initiated");
  console.log("   Amount:", payAmount);
  console.log("   Customer ID:", user?.customerID);
  console.log("   Loan Number:", loan?.loan_number);
  console.log("   Loan Type:", loan?.loan_type);
  console.log("   Module Code:", loan?.loan_module);
  console.log("   Available Gateways:", loanData?.gateways);
  console.log("   Extra Data:", extraData);
  console.log("═══════════════════════════════════════");

  navigate("/order/", {
    state: {
      loan_number: loan.loan_number,
      dueAmount: payAmount,
      customer_id: user.customerID,
      customer_name: user.customerName,
      moduleCode: loan.loan_module,
      loan_desc: loan.loan_desc,
      loan_scheme: loan.loan_scheme,
      loan_type: loan.loan_type,
      loan_amount: loan.loan_amount,
      emi_ewi: loan.emi_ewi,
      gateways: loanData?.gateways,
      ...extraData, // ✅ Merge any extra data (like interestType from Deposit Scheme)
    }
  });
}, [user, loanData, navigate]);


  const handleProductSelection = useCallback((key) => {
    if (loanData?.accounts?.[key]) {
      console.log(`📂 [Dashboard] Selected loan type: ${key}`);
      console.log(`📊 [Dashboard] Number of accounts: ${loanData.accounts[key].length}`);
      setSelectedLoans(loanData.accounts[key]);
      setToggle(key);

      setTimeout(() => {
        loanCardsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [loanData]);
// ✅ UPDATED: Navigate to Transaction History
  const navigateToTransactionHistory = useCallback(() => {
    console.log("═══════════════════════════════════════");
    console.log("📜 [Dashboard] Navigating to Transaction History");
    console.log("   Customer ID:", user?.customerID);
    console.log("   Customer Name:", user?.customerName);
    console.log("   Time:", new Date().toLocaleTimeString());
    console.log("═══════════════════════════════════════");

    if (!user?.customerID) {
      console.error("❌ No customer ID found");
      return;
    }

    navigate("/TransactionHistory", { 
      state: { 
        customer_id: user.customerID,
        customer_name: user.customerName,
        customer_phone: user.cellNumber
      } 
    });
  }, [navigate, user]);

  const navigateToHistory = useCallback(() => {
    console.log("📜 [Dashboard] Navigating to transaction history");
    navigate("/history", { 
      state: { 
        index: 2, 
        token: authState.token, 
        customerID: user?.customerID 
      } 
    });
  }, [navigate, authState.token, user?.customerID]);

  // ==================== SECURITY SCREENS ====================
  
  if (!user) {
    return (
      <AppLayout>
        <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <BootstrapCard className="w-100">
            <CardBody className="text-center p-4">
              <div className="text-warning mb-3" style={{ fontSize: "48px" }}>⚠️</div>
              <h4 className="text-warning mb-3">Session Expired</h4>
              <p className="text-muted">Please log in again to continue.</p>
              <Button variant="primary" onClick={() => navigate("/", { replace: true })}>
                Sign In
              </Button>
            </CardBody>
          </BootstrapCard>
        </Container>
      </AppLayout>
    );
  }

  if (!authState.isValidated) {
    return (
      <AppLayout>
        <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <BootstrapCard className="w-100">
            <CardBody className="text-center p-4">
              <div className="text-danger mb-3" style={{ fontSize: "48px" }}>🔒</div>
              <h4 className="text-danger mb-3">Authentication Required</h4>
              <p className="text-muted">Your session is invalid. Please sign in again.</p>
              <Button onClick={() => { 
                localStorage.clear(); 
                window.location.href = "/"; 
              }}>
                Sign In Again
              </Button>
            </CardBody>
          </BootstrapCard>
        </Container>
      </AppLayout>
    );
  }

  // ==================== RENDER FUNCTIONS ====================

  const renderFinancialProducts = () => {
    const loanTypes = loanData?.loan_types || [];
    
    if (loanTypes.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <Alert severity="warning" sx={{ maxWidth: '500px', width: '100%' }}>
            📭 No loans found for this customer
          </Alert>
        </Box>
      );
    }

    const is3Items = loanTypes.length === 3;

    return (
      <Container 
        maxWidth={is3Items ? 'lg' : (loanTypes.length <= 2 ? 'md' : 'lg')} 
        sx={{ mb: 5, px: { xs: 1, sm: 2, md: 3 } }}
      >
        <Grid 
          container 
          spacing={{ xs: 1, sm: 1.5, md: 2 }} 
          justifyContent="center"
          alignItems="stretch"
        >
          {loanTypes.map(({ key, label, module_code }) => {
            const accountCount = loanData?.accounts?.[key]?.length || 0;
            const isActive = toggle === key;
            
            return (
              <Grid 
                item 
                xs={is3Items ? 4 : 12}
                sm={is3Items ? 4 : 6}
                md={is3Items ? 4 : (loanTypes.length === 1 ? 12 : loanTypes.length === 2 ? 6 : loanTypes.length === 4 ? 6 : 6)}
                key={key}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <StyledLoanCard
                  active={isActive}
                  onClick={() => handleProductSelection(key)}
                  elevation={isActive ? 8 : 2}
                  sx={{ 
                    width: '100%',
                    maxWidth: is3Items ? '100%' : { xs: '100%', sm: '400px' },
                    minHeight: is3Items 
                      ? { xs: '150px', sm: '170px', md: '190px' }
                      : { xs: '180px', sm: '180px', md: '180px' }
                  }}
                >
                  <CardContent 
                    sx={{ 
                      textAlign: "center", 
                      p: is3Items 
                        ? { xs: 1.5, sm: 2, md: 2.5 } 
                        : { xs: 2.5, sm: 3, md: 3.5 },
                      "&:last-child": { 
                        pb: is3Items 
                          ? { xs: 1.5, sm: 2, md: 2.5 } 
                          : { xs: 2.5, sm: 3, md: 3.5 } 
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%'
                    }}
                  >
                    {/* Icon */}
                    <Box 
                      mb={is3Items ? { xs: 0.75, sm: 1, md: 1.5 } : 2}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <img 
                        src={termsvg} 
                        width={is3Items ? (isMobile ? 32 : 38) : (isMobile ? 35 : 40)}
                        alt={label}
                      />
                    </Box>
                    
                    {/* Loan Type Label */}
                    <Typography 
                      variant="h6" 
                      color="primary" 
                      fontWeight="bold" 
                      gutterBottom
                      sx={{
                        fontSize: is3Items 
                          ? { xs: "0.85rem", sm: "1rem", md: "1.1rem" }
                          : { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" },
                        lineHeight: 1.2,
                        mb: { xs: 0.5, sm: 0.75 }
                      }}
                    >
                      {label}
                    </Typography>
                    
                    {/* Module Code */}
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: is3Items ? { xs: 0.75, sm: 1, md: 1.25 } : 1.5,
                        fontSize: is3Items
                          ? { xs: "0.7rem", sm: "0.78rem", md: "0.82rem" }
                          : { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                        fontWeight: 500
                      }}
                    >
                      {module_code}
                    </Typography>
                    
                    {/* Account Count Badge */}
                    {accountCount > 0 && (
                      <Chip
                        label={`${accountCount} account${accountCount > 1 ? 's' : ''}`}
                        color="info"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: is3Items 
                            ? { xs: "0.68rem", sm: "0.72rem", md: "0.75rem" }
                            : { xs: "0.7rem", sm: "0.75rem" },
                          height: is3Items ? { xs: '22px', sm: '24px', md: '26px' } : 'auto',
                          '& .MuiChip-label': {
                            px: is3Items ? { xs: 0.75, sm: 1, md: 1.25 } : 1.5,
                            py: 0.25
                          }
                        }}
                      />
                    )}
                  </CardContent>
                </StyledLoanCard>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    );
  };

  const renderSelectedLoans = () => {
    if (!Array.isArray(selectedLoans)) return null;
    
    if (selectedLoans.length === 0) {
      return (
        <Box ref={loanCardsRef} display="flex" justifyContent="center" py={3} px={2}>
          <Alert severity="warning" sx={{ maxWidth: '500px', width: '100%' }}>
            ℹ️ No accounts found for this loan type.
          </Alert>
        </Box>
      );
    }

    return (
      <Box ref={loanCardsRef}>
        {selectedLoans.map((loan, i) => (
          <BootstrapCard key={`${loan.loan_number}-${i}`} style={{ borderRadius: 10 }} className="mb-3">
            <CardBody style={{ border: "none" }}>
              <Flipcard
                
                loanTypeDesc={loan.loan_type}
                Accnumber={loan.loan_number}
                Disbamt={parseFloat(loan.loan_amount) || 0}
                Instmnt={parseFloat(loan.emi_ewi) || 0}
                Dueamt={parseFloat(loan.net_due_amount) || 0}
                StartDate={loan.start_date ? new Date(loan.start_date).toLocaleDateString() : "N/A"}
                MaturityDate={loan.maturity_date ? new Date(loan.maturity_date).toLocaleDateString() : "N/A"}
                onPay={(amount) => handlePayment(amount, loan)}
                pledgeNetWeight={0}
                Loan={loan}
                moduleCode={loan.loan_module}
                RDredemptionDate="N/A"
                RDinstallmentAmount={parseFloat(loan.emi_ewi) || 0}
                RDdepositType={loan.loan_scheme}
                interestAmountDue={0}
                depositType={loan.loan_scheme}
                currentBalance={parseFloat(loan.net_due_amount) || 0}
                AvilGateways={loanData?.gateways}
              />
            </CardBody>
          </BootstrapCard>
        ))}
      </Box>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <AppLayout>
      <Box sx={{ width: '100%',  
      px: { xs: 2, sm: 3, md: 4 },
      pb: { xs: 12, sm: 14, md: 16 }   }}>
        {/* Header */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          mb={4} 
          mt={3}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Payment Portal
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <IconButton 
              onClick={handleSignOut}
              title="Sign Out"
              sx={{ 
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.08)'
                }
              }}
            >
              <Logout fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Loading State */}
        {isInitializing ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={5}>
            <Box textAlign="center">
              <Spinner animation="border" className="me-2" />
              <Typography variant="body1" sx={{ mt: 2 }}>Loading dashboard...</Typography>
            </Box>
          </Box>
        ) : (
          <>
            {/* Welcome Section */}
            <Box my={4}>
              <Typography variant="h5" gutterBottom>
                Welcome, <span style={{ color: theme.palette.primary.main }}>{user.customerName}</span>
              </Typography>
              <Box sx={{ color: 'text.secondary' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ID: {user.customerID} | Phone: {user.cellNumber}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 4 }}>
              <Chip 
                label="Select Loan Type to Pay"
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  fontWeight: 'normal',
                  px: 2
                }}
              />
            </Divider>

            {renderFinancialProducts()}
          </>
        )}

        {/* Selected Loans Display */}
        {renderSelectedLoans()}
        
        {/* Transaction History Link */}
        <Box 
          className="text-primary mt-4 py-3 text-center fw-bold border p-3 rounded bg-light" 
          sx={{ 
            mb: { xs: 3, sm: 5 },
            cursor: "pointer",
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
          onClick={navigateToHistory}
        >
          📋 View Transaction History
        </Box>
      </Box>

      {/* Back Button Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Leave Dashboard?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to leave? You will be signed out.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Stay Here</Button>
          <Button variant="danger" onClick={handleLeave}>Sign Out</Button>
        </Modal.Footer>
      </Modal>
    </AppLayout>
  );
}
